import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { solicitudesAPI } from '../../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'https://lossaliasfutbolclub.com/api';
const UPLOADS_BASE = API_URL.replace('/api', '') + '/uploads/documentos/';

const SolicitudDetalle = () => {
  const { id } = useParams();
  const [solicitud, setSolicitud] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Modales
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [motivoRechazo, setMotivoRechazo] = useState('');

  useEffect(() => {
    loadSolicitud();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadSolicitud = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await solicitudesAPI.getById(id);
      setSolicitud(response.data);
    } catch (err) {
      setError('Error cargando la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const handleAprobar = async () => {
    try {
      setActionLoading(true);
      setError('');
      const response = await solicitudesAPI.aprobar(id);
      setSuccessMessage(`Solicitud aprobada. Jugador creado con cedula: ${response.data.cedula_jugador}`);
      setShowApproveModal(false);
      await loadSolicitud();
    } catch (err) {
      setError(err.response?.data?.error || 'Error aprobando la solicitud');
      setShowApproveModal(false);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRechazar = async () => {
    if (!motivoRechazo.trim()) {
      setError('Debe indicar el motivo del rechazo');
      return;
    }
    try {
      setActionLoading(true);
      setError('');
      await solicitudesAPI.rechazar(id, motivoRechazo);
      setSuccessMessage('Solicitud rechazada correctamente');
      setShowRejectModal(false);
      setMotivoRechazo('');
      await loadSolicitud();
    } catch (err) {
      setError(err.response?.data?.error || 'Error rechazando la solicitud');
      setShowRejectModal(false);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('es-VE');
  };

  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return '--';
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const m = hoy.getMonth() - nacimiento.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) edad--;
    return edad;
  };

  const isImage = (filename) => /\.(jpg|jpeg|png|webp|heic|heif)$/i.test(filename);

  const Campo = ({ label, value, fullWidth }) => (
    <div className={fullWidth ? 'md:col-span-2' : ''}>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-gray-900 mt-1">{value || '--'}</p>
    </div>
  );

  const CampoBool = ({ label, value }) => (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`mt-1 font-medium ${value ? 'text-green-600' : 'text-gray-400'}`}>
        {value ? 'Si' : 'No'}
      </p>
    </div>
  );

  const getEstadoBadge = (estado) => {
    const estilos = {
      pendiente: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      aprobada: 'bg-green-100 text-green-800 border-green-300',
      rechazada: 'bg-red-100 text-red-800 border-red-300',
    };
    const labels = { pendiente: 'Pendiente', aprobada: 'Aprobada', rechazada: 'Rechazada' };
    return (
      <span className={`px-4 py-1.5 rounded-full text-sm font-bold border ${estilos[estado] || ''}`}>
        {labels[estado] || estado}
      </span>
    );
  };

  const DocumentPreview = ({ label, filename }) => {
    if (!filename) return null;
    const url = UPLOADS_BASE + filename;
    return (
      <div className="border border-gray-200 rounded-lg p-4">
        <p className="text-sm font-semibold text-gray-700 mb-3">{label}</p>
        {isImage(filename) ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            <img src={url} alt={label} className="max-h-64 object-contain rounded-lg border w-full" />
            <p className="text-xs text-lsfc-blue-600 mt-2 hover:underline">Ver en tamano completo</p>
          </a>
        ) : (
          <a href={url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all">
            <span className="text-2xl">📄</span>
            <div>
              <p className="font-medium text-gray-900">Documento PDF</p>
              <p className="text-xs text-lsfc-blue-600 hover:underline">Abrir en nueva pestana</p>
            </div>
          </a>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lsfc-blue-600"></div>
      </div>
    );
  }

  if (error && !solicitud) {
    return (
      <div className="text-center py-20">
        <div className="text-5xl mb-4">⚠️</div>
        <p className="text-red-600 text-lg mb-4">{error}</p>
        <Link to="/admin/solicitudes" className="text-lsfc-blue-600 hover:underline font-medium">
          ← Volver a solicitudes
        </Link>
      </div>
    );
  }

  if (!solicitud) return null;

  const s = solicitud;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <Link to="/admin/solicitudes" className="text-lsfc-blue-600 hover:text-lsfc-blue-800 text-sm font-medium mb-2 inline-block">
            ← Volver a solicitudes
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {s.apellidos} {s.nombres}
          </h1>
          <div className="flex items-center gap-3 mt-2">
            {getEstadoBadge(s.estado)}
            <span className="text-sm text-gray-500">Solicitud #{s.id} - {formatDate(s.created_at)}</span>
          </div>
        </div>
        {s.estado === 'pendiente' && (
          <div className="flex gap-3">
            <button
              onClick={() => setShowRejectModal(true)}
              className="px-5 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium transition-all"
            >
              Rechazar
            </button>
            <button
              onClick={() => setShowApproveModal(true)}
              className="px-5 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium transition-all"
            >
              Aprobar
            </button>
          </div>
        )}
      </div>

      {/* Mensajes */}
      {successMessage && (
        <div className="bg-green-50 border border-green-300 text-green-800 px-4 py-3 rounded-lg font-medium">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Banner post-proceso */}
      {s.estado === 'aprobada' && (
        <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4">
          <p className="font-semibold text-green-800">
            Aprobada el {formatDate(s.fecha_proceso)}
          </p>
          {s.jugador_creado_id && (
            <p className="text-green-700 text-sm mt-1">Jugador creado: #{s.jugador_creado_id}</p>
          )}
        </div>
      )}
      {s.estado === 'rechazada' && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4">
          <p className="font-semibold text-red-800">
            Rechazada el {formatDate(s.fecha_proceso)}
          </p>
          {s.motivo_rechazo && (
            <p className="text-red-700 text-sm mt-1">Motivo: {s.motivo_rechazo}</p>
          )}
        </div>
      )}

      {/* Seccion 1: Datos del Alumno */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-lg font-bold text-lsfc-blue-700 mb-4 flex items-center gap-2">
          <span>⚽</span> Datos del Alumno
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Campo label="Apellidos" value={s.apellidos} />
          <Campo label="Nombres" value={s.nombres} />
          <Campo label="Cedula" value={s.tiene_cedula_propia ? s.cedula : 'No tiene cedula propia'} />
          <Campo label="Sexo" value={s.sexo === 'M' ? 'Masculino' : s.sexo === 'F' ? 'Femenino' : '--'} />
          <Campo label="Fecha de Nacimiento" value={`${formatDate(s.fecha_nacimiento)} (${calcularEdad(s.fecha_nacimiento)} anos)`} />
          <Campo label="Lugar de Nacimiento" value={s.lugar_nacimiento} />
          <Campo label="Estado" value={s.estado_nacimiento} />
          <Campo label="Pais" value={s.pais} />
          <Campo label="Telefono Celular" value={s.telefono_celular} />
          <Campo label="Colegio Actual" value={s.colegio_actual} />
          <Campo label="Direccion" value={s.direccion_habitacion} fullWidth />
          <CampoBool label="Tiene Uniforme" value={s.tiene_uniforme} />
          {s.tiene_uniforme && <Campo label="Numero de Dorsal" value={s.numero_dorsal} />}
        </div>
      </div>

      {/* Seccion 2: Representantes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Padre */}
        <div className={`bg-white rounded-xl shadow-lg p-6 ${s.representante_principal === 'padre' ? 'ring-2 ring-lsfc-blue-500' : ''}`}>
          <h2 className="text-lg font-bold text-blue-700 mb-4 flex items-center gap-2">
            <span>👨</span> Datos del Padre
            {s.representante_principal === 'padre' && (
              <span className="text-xs bg-lsfc-blue-100 text-lsfc-blue-700 px-2 py-0.5 rounded-full font-semibold">PRINCIPAL</span>
            )}
          </h2>
          {s.padre_nombre ? (
            <div className="grid grid-cols-1 gap-3">
              <Campo label="Nombre" value={`${s.padre_nombre} ${s.padre_apellido || ''}`} />
              <Campo label="Cedula" value={s.padre_cedula} />
              <Campo label="Telefono" value={s.padre_telefono} />
              <Campo label="Email" value={s.padre_email} />
              <Campo label="Telefono Oficina" value={s.padre_telefono_oficina} />
              <Campo label="Empresa" value={s.padre_empresa} />
              <Campo label="Cargo" value={s.padre_cargo} />
              <Campo label="Direccion Trabajo" value={s.padre_direccion_trabajo} />
              <Campo label="Telefono Trabajo" value={s.padre_telefono_trabajo} />
            </div>
          ) : (
            <p className="text-gray-400 italic">No se registraron datos del padre</p>
          )}
        </div>

        {/* Madre */}
        <div className={`bg-white rounded-xl shadow-lg p-6 ${s.representante_principal === 'madre' ? 'ring-2 ring-lsfc-blue-500' : ''}`}>
          <h2 className="text-lg font-bold text-pink-700 mb-4 flex items-center gap-2">
            <span>👩</span> Datos de la Madre
            {s.representante_principal === 'madre' && (
              <span className="text-xs bg-lsfc-blue-100 text-lsfc-blue-700 px-2 py-0.5 rounded-full font-semibold">PRINCIPAL</span>
            )}
          </h2>
          {s.madre_nombre ? (
            <div className="grid grid-cols-1 gap-3">
              <Campo label="Nombre" value={`${s.madre_nombre} ${s.madre_apellido || ''}`} />
              <Campo label="Cedula" value={s.madre_cedula} />
              <Campo label="Telefono" value={s.madre_telefono} />
              <Campo label="Email" value={s.madre_email} />
              <Campo label="Telefono Oficina" value={s.madre_telefono_oficina} />
              <Campo label="Empresa" value={s.madre_empresa} />
              <Campo label="Cargo" value={s.madre_cargo} />
              <Campo label="Direccion Trabajo" value={s.madre_direccion_trabajo} />
              <Campo label="Telefono Trabajo" value={s.madre_telefono_trabajo} />
            </div>
          ) : (
            <p className="text-gray-400 italic">No se registraron datos de la madre</p>
          )}
        </div>
      </div>

      {/* Seccion 3: Informacion Medica */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-lg font-bold text-green-700 mb-4 flex items-center gap-2">
          <span>🏥</span> Informacion Medica
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CampoBool label="Tiene Seguro Medico" value={s.tiene_seguro} />
          {s.tiene_seguro && (
            <>
              <Campo label="Compania de Seguro" value={s.compania_seguro} />
              <Campo label="Numero de Poliza" value={s.numero_poliza} />
            </>
          )}
          <CampoBool label="Padece Enfermedades" value={s.padece_enfermedades} />
          {s.padece_enfermedades && (
            <Campo label="Tipo de Enfermedades" value={s.tipo_enfermedades} fullWidth />
          )}
          <Campo label="Alergias" value={s.alergias} fullWidth />
          <Campo label="Tipo de Sangre" value={s.tipo_sangre} />
          <Campo label="Grupo Sanguineo" value={s.grupo_sangre} />
        </div>
      </div>

      {/* Seccion 4: Contactos de Emergencia */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-lg font-bold text-red-700 mb-4 flex items-center gap-2">
          <span>🚨</span> Contactos de Emergencia
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-red-50 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3">Contacto 1</h3>
            <div className="grid grid-cols-1 gap-3">
              <Campo label="Nombre" value={s.emergencia1_nombre} />
              <Campo label="Parentesco" value={s.emergencia1_parentesco} />
              <Campo label="Cedula" value={s.emergencia1_cedula} />
              <Campo label="Telefono" value={s.emergencia1_telefono} />
            </div>
          </div>
          {s.emergencia2_nombre && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-3">Contacto 2</h3>
              <div className="grid grid-cols-1 gap-3">
                <Campo label="Nombre" value={s.emergencia2_nombre} />
                <Campo label="Parentesco" value={s.emergencia2_parentesco} />
                <Campo label="Cedula" value={s.emergencia2_cedula} />
                <Campo label="Telefono" value={s.emergencia2_telefono} />
              </div>
            </div>
          )}
        </div>

        {/* Medicamentos autorizados */}
        <div className="mt-6 bg-purple-50 rounded-lg p-4">
          <h3 className="font-semibold text-purple-800 mb-3">Medicamentos Autorizados</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Campo label="Medicamento para Alergias" value={s.medicamento_alergia} />
            <Campo label="Dosis" value={s.dosis_alergia} />
            <Campo label="Medicamento para Dolor de Cabeza" value={s.medicamento_dolor_cabeza} />
            <Campo label="Dosis" value={s.dosis_dolor_cabeza} />
          </div>
        </div>
      </div>

      {/* Seccion 5: Autorizaciones */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-lg font-bold text-purple-700 mb-4 flex items-center gap-2">
          <span>📝</span> Autorizaciones
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`p-4 rounded-lg border-2 ${s.autorizacion_medica ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
            <p className="font-medium text-sm">{s.autorizacion_medica ? '✅' : '❌'} Autorizacion Medica</p>
          </div>
          <div className={`p-4 rounded-lg border-2 ${s.autorizacion_imagen ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
            <p className="font-medium text-sm">{s.autorizacion_imagen ? '✅' : '❌'} Autorizacion de Imagen</p>
          </div>
          <div className={`p-4 rounded-lg border-2 ${s.autorizacion_medicamentos ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
            <p className="font-medium text-sm">{s.autorizacion_medicamentos ? '✅' : '⬜'} Autorizacion Medicamentos (Opcional)</p>
          </div>
        </div>
      </div>

      {/* Seccion 6: Documentos */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-lg font-bold text-orange-700 mb-4 flex items-center gap-2">
          <span>📄</span> Documentos Adjuntos
        </h2>
        {!s.doc_cedula_alumno && !s.doc_partida_nacimiento && !s.doc_cedula_representante ? (
          <p className="text-gray-400 italic">No se adjuntaron documentos</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <DocumentPreview label="Cedula del Alumno" filename={s.doc_cedula_alumno} />
            <DocumentPreview label="Partida de Nacimiento" filename={s.doc_partida_nacimiento} />
            <DocumentPreview label="Cedula del Representante" filename={s.doc_cedula_representante} />
          </div>
        )}
      </div>

      {/* Botones de accion al final */}
      {s.estado === 'pendiente' && (
        <div className="bg-white rounded-xl shadow-lg p-6 flex flex-col sm:flex-row gap-4 justify-end">
          <button
            onClick={() => setShowRejectModal(true)}
            className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold transition-all"
          >
            Rechazar Solicitud
          </button>
          <button
            onClick={() => setShowApproveModal(true)}
            className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold transition-all"
          >
            Aprobar Solicitud
          </button>
        </div>
      )}

      {/* Modal Aprobar */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Confirmar Aprobacion</h3>
            <p className="text-gray-600 mb-2">
              Se creara automaticamente el registro del jugador y representante(s).
            </p>
            <div className="bg-blue-50 rounded-lg p-3 mb-4">
              <p className="font-semibold text-blue-800">{s.nombres} {s.apellidos}</p>
              <p className="text-sm text-blue-600">Cedula: {s.cedula || 'Se generara automaticamente'}</p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowApproveModal(false)}
                disabled={actionLoading}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleAprobar}
                disabled={actionLoading}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium disabled:opacity-50"
              >
                {actionLoading ? 'Procesando...' : 'Confirmar Aprobacion'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Rechazar */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Rechazar Solicitud</h3>
            <p className="text-gray-600 mb-3">
              Indique el motivo del rechazo para <strong>{s.nombres} {s.apellidos}</strong>:
            </p>
            <textarea
              value={motivoRechazo}
              onChange={(e) => setMotivoRechazo(e.target.value)}
              placeholder="Motivo del rechazo..."
              rows="4"
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:outline-none resize-none"
            />
            <div className="flex gap-3 justify-end mt-4">
              <button
                onClick={() => { setShowRejectModal(false); setMotivoRechazo(''); }}
                disabled={actionLoading}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleRechazar}
                disabled={actionLoading || !motivoRechazo.trim()}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium disabled:opacity-50"
              >
                {actionLoading ? 'Procesando...' : 'Confirmar Rechazo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SolicitudDetalle;
